import {
  getConnectionByInteraction,
  getParentRole,
  getActivity,
  getConnections,
  getAllActivitiesWithRole,
  getAllPartThreadsWithRole,
  getStateAncestors,
  getStateSuccessors,
  getActivityByPoststate,
  getActivityByPrestate,
  getThreadByState,
  getThreadSequence,
  getActivitiesForward,
  getThread,
  getAncestorThreads,
  getSubthreads,
  getThreadActivities,
  getStateById,
} from "./radUtils.js";
import { createDebugLogger } from "./debug.js";
import { legacyToVnext } from "./vnext/rad-compat-runtime.js";
import {
  buildReachabilityContext,
  buildReachabilityGraph,
  computeReachabilityFrontier,
} from "./vnext/rad-reachability-runtime.js";
import {
  buildTargetIndex,
  collectCandidateTargetIds,
  hydrateThreadSource,
  resolveConnectionScope,
  resolveSourceRoleId,
  resolveTargetRoleId,
} from "./rad-target-source-normalization.js";

const debugTargets = createDebugLogger("RAD_DEBUG_TARGETS");

const isReachabilityEngineEnabled = (rad) => {
  const mode = rad?.info?.targetEngine;
  return mode === "reachability" || mode === "hybrid";
};

const sortUniqueIds = (ids) =>
  [...new Set((ids ?? []).filter(Boolean).map((id) => String(id)))].sort();

const mapFrontierBlockedReasonToLegacy = (reason) => {
  switch (reason) {
    case "blocked_case_scope":
    case "blocked_part_case_scope":
      return "refinement_crossing_scope";
    case "blocked_by_first_interaction_guard":
      return "state_temporal_successor";
    case "blocked_same_role_scope":
      return "same_role_scope";
    case "blocked_past_time":
      return "state_temporal_ancestor";
    case "not_reachable":
    default:
      return "excluded_by_state_rules";
  }
};

const findNearestPrecedingInteractionSeeds = (
  graph,
  context,
  sourceStateId,
  sourceRoleId,
) => {
  const startNodeId = `state:${sourceStateId}`;
  if (!graph.nodes[startNodeId]) return [];

  const queue = [{ nodeId: startNodeId, depth: 0 }];
  const visited = new Set([startNodeId]);
  const seedIds = new Set();
  let minDepth = Number.POSITIVE_INFINITY;

  while (queue.length > 0) {
    const { nodeId, depth } = queue.shift();
    if (depth >= minDepth) continue;

    const incomingEdgeIds = graph.incoming[nodeId] ?? [];
    incomingEdgeIds.forEach((edgeId) => {
      const edge = graph.edges[edgeId];
      if (!edge || edge.kind === "sync") return;

      const fromNodeId = edge.from;
      if (visited.has(fromNodeId)) return;
      const fromNode = graph.nodes[fromNodeId];
      if (!fromNode) return;
      if (sourceRoleId && fromNode.roleId !== sourceRoleId) return;

      const nextDepth = depth + 1;
      if (
        fromNode.kind === "activity_post" &&
        fromNode.actionId &&
        context.actionsById[fromNode.actionId]?.kind === "interaction"
      ) {
        if (nextDepth < minDepth) {
          minDepth = nextDepth;
          seedIds.clear();
        }
        if (nextDepth === minDepth) {
          seedIds.add(fromNode.actionId);
        }
        return;
      }

      visited.add(fromNodeId);
      queue.push({ nodeId: fromNodeId, depth: nextDepth });
    });
  }

  return sortUniqueIds([...seedIds]);
};

const evaluateStateTargetRulesReachability = (rad, source) => {
  const sourceThread = getThreadByState(rad)(source?.id);
  const sourceParentRole = sourceThread
    ? getParentRole(rad)(sourceThread.id)
    : null;
  if (!sourceThread || !sourceParentRole) {
    return { allowedIds: [], exclusionReasons: {}, warnings: [] };
  }

  const vnextRad = legacyToVnext(rad);
  const context = buildReachabilityContext(vnextRad);
  const graph = buildReachabilityGraph(vnextRad, context);
  const sourcePostActivity = getActivityByPrestate(rad)(source.id);
  const isTerminalSource = !sourcePostActivity;
  const frontierSources = [{ id: source.id, class: "state" }];
  if (isTerminalSource) {
    const predecessorSeeds = findNearestPrecedingInteractionSeeds(
      graph,
      context,
      source.id,
      sourceParentRole.id,
    );
    predecessorSeeds.forEach((interactionId) =>
      frontierSources.push({ id: interactionId, class: "activity" }),
    );
  }
  if (sourceThread.class !== "thread") {
    // For non-thread state sources, we also need to consider the
    // connection scope of the nearest preceding interaction.
    let topRefinement = null;
    let traversalThread = sourceThread;
    while (traversalThread && traversalThread.class !== "thread") {
      const parentRefinement = getActivity(rad)(traversalThread.parent_id);
      if (!parentRefinement) break;
      topRefinement = parentRefinement;
      traversalThread = getThread(rad)(parentRefinement.thread_id);
    }
    const blockingInteraction = topRefinement?.prestate_id
      ? getActivityByPoststate(rad)(topRefinement.prestate_id)
      : null;
    if (blockingInteraction?.type === "interaction") {
      const connectionIds = getConnections(rad)
        .filter((connection) =>
          connection.interactions.includes(blockingInteraction.id),
        )
        .map((connection) => connection.id);
      connectionIds.forEach((connectionId) => {
        const connection = getConnections(rad).find(
          (con) => con.id === connectionId,
        );
        (connection?.interactions ?? []).forEach((interactionId) => {
          const interaction = getActivity(rad)(interactionId);
          if (!interaction || interaction.type !== "interaction") return;
          const interactionRole = getParentRole(rad)(interaction.thread_id);
          if (!interactionRole || interactionRole.id === sourceParentRole.id)
            return;
          frontierSources.push({ id: interaction.id, class: "activity" });
        });
      });
    }
  }

  const frontierDebug = [];
  const mergedFrontier = frontierSources.reduce(
    (acc, frontierSource) => {
      const frontier = computeReachabilityFrontier(
        graph,
        context,
        frontierSource,
        {},
      );
      frontierDebug.push({
        source: frontierSource,
        allowedRoleIds: sortUniqueIds(frontier.allowedRoleIds),
        allowedActionIds: sortUniqueIds(frontier.allowedActionIds),
        allowedStateIds: sortUniqueIds(frontier.allowedStateIds),
        blocked: frontier.blocked ?? {},
      });
      frontier.allowedRoleIds.forEach((id) => acc.allowedRoleIds.add(id));
      frontier.allowedActionIds.forEach((id) => acc.allowedActionIds.add(id));
      frontier.allowedStateIds.forEach((id) => acc.allowedStateIds.add(id));
      Object.entries(frontier.blocked ?? {}).forEach(([id, reason]) => {
        if (!acc.blocked[id]) {
          acc.blocked[id] = reason;
        }
      });
      return acc;
    },
    {
      allowedRoleIds: new Set(),
      allowedActionIds: new Set(),
      allowedStateIds: new Set(),
      blocked: {},
    },
  );

  const index = buildTargetIndex(rad);
  const viableRoleIDs = (rad.roles ?? [])
    .filter((role) => role.id !== sourceParentRole.id)
    .map((role) => role.id);
  const legacyDetails = !isTerminalSource
    ? getStateTargetDetails(rad, source)
    : null;
  const legacyExcludeSet = new Set(legacyDetails?.excludeElements ?? []);
  const legacyFrontier = legacyDetails?.subthreadFrontier ?? null;
  const legacyAllowedRoleIDs =
    legacyFrontier?.roleIds?.length && !isTerminalSource
      ? viableRoleIDs.filter((roleId) =>
          legacyFrontier.roleIds.includes(roleId),
        )
      : viableRoleIDs;
  const sourceRoleInteractionIds = getAllActivitiesWithRole(rad)
    .filter(
      (a) => a.parentRoleId === sourceParentRole.id && a.type === "interaction",
    )
    .map((a) => a.id);
  const connectedInteractions = new Set(
    getConnections(rad)
      .filter((con) =>
        con.interactions.some((id) => sourceRoleInteractionIds.includes(id)),
      )
      .flatMap((con) => con.interactions),
  );

  const allowedIds = [
    ...new Set([
      ...legacyAllowedRoleIDs,
      ...[...mergedFrontier.allowedActionIds].filter((actionId) => {
        const activity = getActivity(rad)(actionId);
        if (!activity || activity.type !== "interaction") return false;
        if (connectedInteractions.has(actionId)) return false;
        const targetRoleId = resolveTargetRoleId(rad, index, actionId);
        if (targetRoleId === sourceParentRole.id) return false;
        if (!isTerminalSource) {
          if (legacyExcludeSet.has(actionId)) return false;
          if (
            legacyFrontier?.interactionIds?.length &&
            !legacyFrontier.interactionIds.includes(actionId)
          ) {
            return false;
          }
        }
        return true;
      }),
      ...[...mergedFrontier.allowedStateIds].filter((stateId) => {
        if (stateId === source.id) return false;
        const targetRoleId = resolveTargetRoleId(rad, index, stateId);
        if (targetRoleId === sourceParentRole.id) return false;
        if (!isTerminalSource) {
          if (legacyExcludeSet.has(stateId)) return false;
          if (
            legacyFrontier?.stateIds?.length &&
            !legacyFrontier.stateIds.includes(stateId)
          ) {
            return false;
          }
          return true;
        }
        // Terminal state sources should only project to terminal states in other roles.
        return !getActivityByPrestate(rad)(stateId);
      }),
    ]),
  ];

  const allowedSet = new Set(allowedIds);
  const exclusionReasons = {};
  for (const targetId of collectCandidateTargetIds(rad)) {
    if (allowedSet.has(targetId)) continue;
    if (targetId === source.id) {
      exclusionReasons[targetId] = "source_self";
      continue;
    }
    const targetRoleId = resolveTargetRoleId(rad, index, targetId);
    if (targetRoleId && targetRoleId === sourceParentRole.id) {
      exclusionReasons[targetId] = "same_role_scope";
      continue;
    }
    if (connectedInteractions.has(targetId)) {
      exclusionReasons[targetId] = "state_connection_scope";
      continue;
    }
    const frontierReason = mergedFrontier.blocked?.[targetId];
    exclusionReasons[targetId] =
      mapFrontierBlockedReasonToLegacy(frontierReason);
  }

  return {
    allowedIds,
    exclusionReasons,
    warnings: [],
    trace: {
      engine: "reachability",
      source,
      sourceThreadId: sourceThread.id,
      sourceRoleId: sourceParentRole.id,
      frontierSources,
      frontierBySource: frontierDebug,
      mergedFrontier: {
        allowedRoleIds: sortUniqueIds([...mergedFrontier.allowedRoleIds]),
        allowedActionIds: sortUniqueIds([...mergedFrontier.allowedActionIds]),
        allowedStateIds: sortUniqueIds([...mergedFrontier.allowedStateIds]),
        blocked: mergedFrontier.blocked,
      },
    },
  };
};

const evaluateStateTargetRulesLegacy = (rad, source) => {
  const details = getStateTargetDetails(rad, source);
  if (!details) {
    return { allowedIds: [], exclusionReasons: {}, warnings: [] };
  }

  const viableInteractions = details.allActivities
    .filter((a) => a.type === "interaction")
    .filter((a) => !details.connectedInteractions.includes(a.id))
    .filter((a) => !details.excludeElements.includes(a.id))
    .filter((a) =>
      details.subthreadFrontier?.interactionIds?.length
        ? details.subthreadFrontier.interactionIds.includes(a.id)
        : true,
    )
    .map((a) => a.id);

  const viableStates = details.allActivities
    .map((a) => [a.prestate_id, a.poststate_id])
    .flat()
    .filter(
      (s) =>
        s !== null && s !== undefined && !details.excludeElements.includes(s),
    )
    .filter((s) =>
      details.subthreadFrontier?.stateIds?.length
        ? details.subthreadFrontier.stateIds.includes(s)
        : true,
    )
    .flat();

  const viableRoleIDs = details.subthreadFrontier?.roleIds?.length
    ? details.viableRoleIDs.filter((roleId) =>
        details.subthreadFrontier.roleIds.includes(roleId),
      )
    : details.viableRoleIDs;

  const allowedIds = [
    ...new Set([...viableRoleIDs, ...viableInteractions, ...viableStates]),
  ];
  const allowedSet = new Set(allowedIds);
  const exclusionReasons = {};
  const index = buildTargetIndex(rad);
  const connectedInteractions = new Set(details.connectedInteractions);
  const untargetAncestors = new Set(details.untargetAncestors);
  const untargetSuccessors = new Set(details.untargetSuccessors);
  const refinementExclusions = new Set(details.refinementExclusions ?? []);

  for (const targetId of collectCandidateTargetIds(rad)) {
    if (allowedSet.has(targetId)) continue;
    if (targetId === source.id) {
      exclusionReasons[targetId] = "source_self";
      continue;
    }
    const targetRoleId = resolveTargetRoleId(rad, index, targetId);
    if (targetRoleId && targetRoleId === details.parentRoleId) {
      exclusionReasons[targetId] = "same_role_scope";
      continue;
    }
    if (connectedInteractions.has(targetId)) {
      exclusionReasons[targetId] = "state_connection_scope";
      continue;
    }
    if (untargetAncestors.has(targetId)) {
      exclusionReasons[targetId] = "state_temporal_ancestor";
      continue;
    }
    if (untargetSuccessors.has(targetId)) {
      exclusionReasons[targetId] = "state_temporal_successor";
      continue;
    }
    if (refinementExclusions.has(targetId)) {
      exclusionReasons[targetId] = "refinement_crossing_scope";
      continue;
    }
    exclusionReasons[targetId] = "excluded_by_state_rules";
  }

  return { allowedIds, exclusionReasons, warnings: [] };
};

const getConnectionTargets = (rad) => (connection) => {
  if (!connection?.id) {
    debugTargets("getConnectionTargets - Invalid connection");
    return [];
  }
  const connectedRoles = connection
    ? connection.interactions.map((c) => {
        const activity = getActivity(rad)(c);
        const parentRole = getParentRole(rad)(activity.thread_id);
        return parentRole ? parentRole.id : null;
      })
    : [];

  const viableRoles = (rad.roles ?? [])
    .filter((role) => !connectedRoles.includes(role.id))
    .map((role) => role.id);

  const allActivities = getAllActivitiesWithRole(rad);
  const sourceRoleActivities = allActivities.filter((a) =>
    connectedRoles.includes(a.parentRoleId),
  );
  const excludeActivities = [
    ...sourceRoleActivities
      .map((a) => [a.id, a.prestate_id, a.poststate_id])
      .flat(),
  ].flat();

  const allPartThreads = getAllPartThreadsWithRole(rad);
  const sourceRolePartThreads = allPartThreads.filter((t) =>
    connectedRoles.includes(t.parentRoleId),
  );
  const excludePartThreads = [...sourceRolePartThreads.map((t) => t.id)].flat();

  const startActivity = getActivity(rad)(connection.interactions[0]);
  const untargetAncestors = getStateAncestors(rad)(startActivity.prestate_id);
  const untargetSuccessors = getStateSuccessors(rad)(
    startActivity.poststate_id,
  );

  const excludeElements = [
    ...new Set([
      ...excludeActivities,
      ...excludePartThreads,
      ...untargetAncestors,
      ...untargetSuccessors,
    ]),
  ].filter(Boolean);

  const viableInteractions = allActivities
    .filter((a) => a.type === "interaction")
    .filter((a) => !excludeElements.includes(a.id))
    .map((a) => a.id);
  const viableThreads = allPartThreads
    .filter((a) => !excludeElements.includes(a.id))
    .map((a) => a.id);
  const viableStates = allActivities
    .map((a) => [a.prestate_id, a.poststate_id])
    .flat()
    .filter(
      (s) => s !== null && s !== undefined && !excludeElements.includes(s),
    )
    .flat();

  return [
    ...viableRoles,
    ...viableInteractions,
    ...viableThreads,
    ...viableStates,
  ];
};

const evaluateRoleTargetRules = (rad, source) => {
  if (!source?.id) {
    return { allowedIds: [], exclusionReasons: {} };
  }
  const index = buildTargetIndex(rad);

  const viableRoleIDs = (rad.roles ?? [])
    .filter((role) => role.id !== source.id)
    .map((role) => role.id);

  const allInteractions = rad.activities
    .filter((a) => a.type === "interaction")
    .map((a) => {
      const aParentRole = getParentRole(rad)(a.thread_id);
      return { id: a.id, parentRoleId: aParentRole?.id };
    });

  const sourceRoleInteractionIds = allInteractions
    .filter((a) => a.parentRoleId === source.id)
    .map((a) => a.id);

  const connectedInteractionIds = getConnections(rad)
    .filter((con) =>
      con.interactions.some((id) => sourceRoleInteractionIds.includes(id)),
    )
    .flatMap((con) => con.interactions)
    .filter((id) => !sourceRoleInteractionIds.includes(id));

  const excludeInteractions = [
    ...sourceRoleInteractionIds,
    ...connectedInteractionIds,
  ];

  const viableInteractions = allInteractions
    .filter((a) => !excludeInteractions.includes(a.id))
    .map((a) => a.id);

  const allStates = (rad.states ?? []).map((s) => s.id);
  const viableStates = allStates.filter(
    (stateId) => resolveTargetRoleId(rad, index, stateId) !== source.id,
  );

  const allowedIds = [...viableRoleIDs, ...viableInteractions, ...viableStates];
  const allowedSet = new Set(allowedIds);
  const exclusionReasons = {};

  for (const targetId of collectCandidateTargetIds(rad)) {
    if (allowedSet.has(targetId)) continue;
    if (targetId === source.id) {
      exclusionReasons[targetId] = "source_self";
      continue;
    }
    const targetRoleId = resolveTargetRoleId(rad, index, targetId);
    if (targetRoleId && targetRoleId === source.id) {
      exclusionReasons[targetId] = "same_role_scope";
      continue;
    }
    exclusionReasons[targetId] = "excluded_by_role_rules";
  }

  return { allowedIds, exclusionReasons };
};

export const getStateTargetDetails = (rad, source) => {
  if (!source?.id) {
    return null;
  }
  const currentThread = getThreadByState(rad)(source.id);
  if (!currentThread) {
    return null;
  }
  const parentRole = getParentRole(rad)(currentThread.id);
  if (!parentRole) {
    return null;
  }

  const connectedInteractions = getConnections(rad)
    .filter((con) =>
      con.interactions.some((id) => {
        const activity = getActivity(rad)(id);
        return getParentRole(rad)(activity.thread_id)?.id === parentRole.id;
      }),
    )
    .map((con) => con.interactions)
    .flat();

  const allActivities = getAllActivitiesWithRole(rad);
  const sourceRoleActivities = allActivities.filter(
    (a) => a.parentRoleId === parentRole.id,
  );
  const excludeActivities = [
    ...sourceRoleActivities
      .map((a) => [a.id, a.prestate_id, a.poststate_id])
      .flat(),
  ].flat();

  const viableRoleIDs = (rad.roles ?? [])
    .filter((role) => role.id !== parentRole.id)
    .map((role) => role.id);

  const preActivity = getActivityByPoststate(rad)(source.id);
  const shouldComputeAncestors =
    Boolean(preActivity) || currentThread.class !== "thread";
  const untargetAncestors = !shouldComputeAncestors
    ? []
    : getStateAncestors(rad)(source.id) || [];

  const collectRefinementDescendantStates = (
    refinementId,
    visitedThreadIds = new Set(),
  ) => {
    const ids = [];
    const subthreads = getSubthreads(rad)(refinementId);
    subthreads.forEach((subthread) => {
      if (!subthread?.id || visitedThreadIds.has(subthread.id)) {
        return;
      }
      visitedThreadIds.add(subthread.id);
      const activities = getThreadActivities(rad)(subthread.id);
      activities.forEach((activity) => {
        ids.push(activity.prestate_id, activity.poststate_id);
        if (activity.type === "interaction") {
          ids.push(activity.id);
        }
        if (activity.class === "refinement") {
          ids.push(
            ...collectRefinementDescendantStates(activity.id, visitedThreadIds),
          );
        }
      });
    });
    return ids;
  };

  const collectAncestorSuccessorExclusions = (ancestorIds) => {
    const ids = [];
    ancestorIds.forEach((ancestorId) => {
      const ancestor = getActivity(rad)(ancestorId);
      if (!ancestor || ancestor.type !== "interaction") {
        return;
      }
      const forwardActivities = getActivitiesForward(rad)(
        ancestor.thread_id,
        ancestor.prestate_id,
      );
      forwardActivities.forEach((activity) => {
        if (activity.type === "interaction") {
          ids.push(activity.id);
        }
        ids.push(activity.poststate_id);
        if (activity.class === "refinement") {
          ids.push(...collectRefinementDescendantStates(activity.id));
        }
      });
    });
    return ids.filter(Boolean);
  };

  const ancestorSuccessorExclusions =
    currentThread.class !== "thread"
      ? collectAncestorSuccessorExclusions(untargetAncestors)
      : [];

  const collectBlockingClosureExclusions = () => {
    if (currentThread.class === "thread") {
      return [];
    }

    const collectInteractionClosure = (seedInteractionId) => {
      if (!seedInteractionId) return [];
      const seen = new Set([seedInteractionId]);
      const queue = [seedInteractionId];

      while (queue.length > 0) {
        const currentId = queue.shift();
        const connections = getConnections(rad).filter((con) =>
          con.interactions.includes(currentId),
        );
        connections.forEach((connection) => {
          connection.interactions.forEach((interactionId) => {
            if (!seen.has(interactionId)) {
              seen.add(interactionId);
              queue.push(interactionId);
            }
          });
        });
      }
      return [...seen];
    };

    const collectInteractionScope = (interaction) => {
      if (!interaction) return [];

      const ids = [];
      const threadSequence = getThreadSequence(rad)(interaction.thread_id);
      if (!threadSequence.length) {
        return ids;
      }

      let reachedInteraction = false;
      threadSequence.forEach((activity) => {
        if (!reachedInteraction) {
          if (activity.type === "interaction") {
            ids.push(activity.id);
          }
          ids.push(activity.prestate_id);
        }
        if (activity.id === interaction.id) {
          reachedInteraction = true;
        }
      });

      const forwardActivities = getActivitiesForward(rad)(
        interaction.thread_id,
        interaction.prestate_id,
      );
      forwardActivities.forEach((activity) => {
        if (activity.type === "interaction") {
          ids.push(activity.id);
        }
        ids.push(activity.poststate_id);
        if (activity.class === "refinement") {
          ids.push(...collectRefinementDescendantStates(activity.id));
        }
      });

      return ids;
    };

    let topRefinement = null;
    let traversalThread = currentThread;
    while (traversalThread && traversalThread.class !== "thread") {
      const parentRefinement = getActivity(rad)(traversalThread.parent_id);
      if (!parentRefinement) {
        break;
      }
      topRefinement = parentRefinement;
      traversalThread = getThread(rad)(parentRefinement.thread_id);
    }

    if (!topRefinement?.prestate_id) {
      return [];
    }

    const blockingInteraction = getActivityByPoststate(rad)(
      topRefinement.prestate_id,
    );
    if (!blockingInteraction || blockingInteraction.type !== "interaction") {
      return [];
    }

    const closureInteractionIds = collectInteractionClosure(
      blockingInteraction.id,
    );
    return closureInteractionIds
      .flatMap((interactionId) =>
        collectInteractionScope(getActivity(rad)(interactionId)),
      )
      .filter(Boolean);
  };

  const blockingClosureExclusions =
    currentThread.class !== "thread" ? collectBlockingClosureExclusions() : [];

  const collectSubthreadConnectionFrontier = () => {
    if (currentThread.class === "thread") {
      return null;
    }

    let topRefinement = null;
    let traversalThread = currentThread;
    while (traversalThread && traversalThread.class !== "thread") {
      const parentRefinement = getActivity(rad)(traversalThread.parent_id);
      if (!parentRefinement) break;
      topRefinement = parentRefinement;
      traversalThread = getThread(rad)(parentRefinement.thread_id);
    }

    if (!topRefinement?.prestate_id) {
      return null;
    }

    const blockingInteraction = getActivityByPoststate(rad)(
      topRefinement.prestate_id,
    );
    if (!blockingInteraction || blockingInteraction.type !== "interaction") {
      return null;
    }

    const frontierRoleIds = new Set();
    const frontierInteractionIds = new Set();
    const frontierStateIds = new Set();
    const blockingConnections = getConnections(rad).filter((connection) =>
      connection.interactions.includes(blockingInteraction.id),
    );

    blockingConnections.forEach((connection) => {
      connection.interactions.forEach((interactionId) => {
        const interaction = getActivity(rad)(interactionId);
        if (!interaction || interaction.type !== "interaction") {
          return;
        }
        const interactionRole = getParentRole(rad)(interaction.thread_id);
        if (!interactionRole || interactionRole.id === parentRole.id) {
          return;
        }

        frontierRoleIds.add(interactionRole.id);
        const pushThreadContinuation = (thread, startStateId) => {
          if (!thread || !startStateId) return;
          frontierStateIds.add(startStateId);
          const forwardActivities = getThreadSequence(rad)(
            thread.id,
            startStateId,
          );
          forwardActivities.forEach((activity) => {
            if (activity.type === "interaction") {
              frontierInteractionIds.add(activity.id);
            }
            if (activity.prestate_id) {
              frontierStateIds.add(activity.prestate_id);
            }
            if (activity.poststate_id) {
              frontierStateIds.add(activity.poststate_id);
            }
            if (activity.class === "refinement") {
              collectRefinementDescendantStates(activity.id).forEach((id) => {
                frontierStateIds.add(id);
              });
            }
          });
        };

        const interactionThread = getThread(rad)(interaction.thread_id);
        pushThreadContinuation(interactionThread, interaction.poststate_id);

        let traversalThread = interactionThread;
        while (traversalThread && traversalThread.class !== "thread") {
          const parentRefinement = getActivity(rad)(traversalThread.parent_id);
          if (!parentRefinement) break;
          const parentThread = getThread(rad)(parentRefinement.thread_id);
          pushThreadContinuation(parentThread, parentRefinement.poststate_id);
          traversalThread = parentThread;
        }
      });
    });

    if (frontierRoleIds.size === 0) {
      return null;
    }

    return {
      roleIds: [...frontierRoleIds],
      interactionIds: [...frontierInteractionIds],
      stateIds: [...frontierStateIds],
    };
  };

  const subthreadFrontier =
    currentThread.class !== "thread"
      ? collectSubthreadConnectionFrontier()
      : null;

  const postActivity = getActivityByPrestate(rad)(source.id);
  const untargetSuccessors = !postActivity
    ? []
    : getStateSuccessors(rad)(source.id) || [];

  const excludeElements = [
    ...new Set([
      ...excludeActivities,
      ...untargetAncestors,
      ...ancestorSuccessorExclusions,
      ...blockingClosureExclusions,
      ...untargetSuccessors,
    ]),
  ].filter(Boolean);

  const sourceRoleInteractionIds = allActivities
    .filter((a) => a.parentRoleId === parentRole.id && a.type === "interaction")
    .map((a) => a.id);

  const sourceScopedConnections = getConnections(rad).filter((con) =>
    con.interactions.some((id) => sourceRoleInteractionIds.includes(id)),
  );
  const connectedRemoteInteractions = sourceScopedConnections
    .flatMap((con) => con.interactions)
    .filter((id) => !sourceRoleInteractionIds.includes(id));

  const linkedPartThreads = connectedRemoteInteractions
    .map((id) => getActivity(rad)(id))
    .filter(Boolean)
    .map((activity) => getThread(rad)(activity.thread_id))
    .filter((thread) => thread?.class === "part");

  const refinementExclusions = [];
  const refinementStateOverrides = [];
  const pushThreadExclusions = (thread) => {
    if (!thread) return;
    refinementExclusions.push(thread.id);
    const threadActivities = getThreadActivities(rad)(thread.id);
    threadActivities.forEach((activity) => {
      refinementExclusions.push(
        activity.id,
        activity.prestate_id,
        activity.poststate_id,
      );
    });
  };

  const getAdmissibleStatesBeforeInteraction = (thread, interaction) => {
    if (!thread || !interaction) return [];
    const sequence = getThreadSequence(rad)(thread.id, thread.prestate_id);
    if (!sequence.length) {
      return interaction.prestate_id ? [interaction.prestate_id] : [];
    }

    const admissible = [];
    let foundInteraction = false;
    for (const activity of sequence) {
      if (activity.prestate_id) {
        admissible.push(activity.prestate_id);
      }
      if (activity.id === interaction.id) {
        foundInteraction = true;
        break;
      }
    }
    if (!foundInteraction && interaction.prestate_id) {
      admissible.push(interaction.prestate_id);
    } else if (!admissible.length && interaction.prestate_id) {
      admissible.push(interaction.prestate_id);
    }
    return admissible;
  };

  const siblingInteractionIds = [];
  linkedPartThreads.forEach((partThread) => {
    const siblings = getSubthreads(rad)(partThread.parent_id).filter(
      (thread) => thread.id !== partThread.id,
    );
    siblings.forEach((sibling) => {
      pushThreadExclusions(sibling);
      const interactions = getThreadActivities(rad)(sibling.id)
        .filter((activity) => activity.type === "interaction")
        .map((activity) => activity.id);
      siblingInteractionIds.push(...interactions);
    });
  });

  const siblingConnectedInteractions = getConnections(rad)
    .filter((con) =>
      con.interactions.some((id) => siblingInteractionIds.includes(id)),
    )
    .flatMap((con) => con.interactions)
    .filter((id) => !siblingInteractionIds.includes(id));

  siblingConnectedInteractions.forEach((interactionId) => {
    const interaction = getActivity(rad)(interactionId);
    if (!interaction) return;
    const targetThread = getThread(rad)(interaction.thread_id);
    pushThreadExclusions(targetThread);
    refinementStateOverrides.push(
      ...getAdmissibleStatesBeforeInteraction(targetThread, interaction),
    );
  });

  const refinementExclusionSet = new Set(refinementExclusions.filter(Boolean));
  refinementStateOverrides.filter(Boolean).forEach((stateId) => {
    refinementExclusionSet.delete(stateId);
  });

  const allExclusionSet = new Set([
    ...excludeElements,
    ...refinementExclusionSet,
  ]);
  refinementStateOverrides.filter(Boolean).forEach((stateId) => {
    allExclusionSet.delete(stateId);
  });
  if (subthreadFrontier) {
    subthreadFrontier.interactionIds.forEach((id) =>
      allExclusionSet.delete(id),
    );
    subthreadFrontier.stateIds.forEach((id) => allExclusionSet.delete(id));
  }
  const allExclusions = [...allExclusionSet].filter(Boolean);

  return {
    parentRoleId: parentRole.id,
    connectedInteractions,
    allActivities,
    viableRoleIDs,
    untargetAncestors,
    untargetSuccessors,
    excludeElements: allExclusions,
    refinementExclusions: [...new Set(refinementExclusionSet)].filter(Boolean),
    refinementStateOverrides: [...new Set(refinementStateOverrides)].filter(
      Boolean,
    ),
    subthreadFrontier,
  };
};

const evaluateStateTargetRules = (rad, source) => {
  const mode = rad?.info?.targetEngine;
  if (mode === "legacy") return evaluateStateTargetRulesLegacy(rad, source);
  if (mode === "hybrid") {
    const reachability = evaluateStateTargetRulesReachability(rad, source);
    const legacy = evaluateStateTargetRulesLegacy(rad, source);
    const legacySet = new Set(legacy.allowedIds);
    const reachSet = new Set(reachability.allowedIds);
    const onlyLegacy = legacy.allowedIds.filter((id) => !reachSet.has(id));
    const onlyReach = reachability.allowedIds.filter(
      (id) => !legacySet.has(id),
    );
    const warnings = [
      `target_engine_diff:legacy_only=${onlyLegacy.length};reachability_only=${onlyReach.length}`,
    ];
    return {
      allowedIds: reachability.allowedIds,
      exclusionReasons: reachability.exclusionReasons,
      warnings,
      trace: reachability.trace,
    };
  }
  return evaluateStateTargetRulesReachability(rad, source);
};

const getPartTargets = (rad) => (source) => {
  if (!source?.id) return [];

  const parentRole = getParentRole(rad)(source.id);
  if (!parentRole) return [];

  const parentRefinement = getActivity(rad)(source.parent_id);
  if (!parentRefinement) return [];

  const connectedInteractions = getConnections(rad)
    .filter((con) =>
      con.interactions.some((id) => {
        const activity = getActivity(rad)(id);
        return getParentRole(rad)(activity.thread_id)?.id === parentRole.id;
      }),
    )
    .map((con) => con.interactions)
    .flat();

  const allActivities = getAllActivitiesWithRole(rad);
  const allPartThreads = getAllPartThreadsWithRole(rad);

  const sourceRoleActivities = allActivities.filter(
    (a) =>
      a.parentRoleId === getParentRole(rad)(parentRefinement.thread_id)?.id,
  );
  const sourceRolePartThreads = allPartThreads.filter(
    (t) =>
      t.parentRoleId === getParentRole(rad)(parentRefinement.thread_id)?.id,
  );
  const excludePartThreads = [...sourceRolePartThreads.map((t) => t.id)].flat();
  const excludeActivities = [
    ...sourceRoleActivities
      .map((a) => [a.id, a.prestate_id, a.poststate_id])
      .flat(),
  ]
    .flat()
    .filter(Boolean);

  const viableRoleIDs = (rad.roles ?? [])
    .filter((role) => role.id !== parentRole.id)
    .map((role) => role.id);

  const untargetAncestors = getStateAncestors(rad)(
    parentRefinement.prestate_id,
  );
  const untargetSuccessors = getStateSuccessors(rad)(
    parentRefinement.poststate_id,
  );

  const siblingPartThreads = getSubthreads(rad)(parentRefinement.id).filter(
    (t) => t.id !== source.id,
  );
  const siblingPartThreadInteractions = siblingPartThreads
    .map((t) => getThreadActivities(rad)(t.id))
    .flat()
    .filter((a) => a.type === "interaction")
    .map((a) => a.id);
  const siblingConnections = getConnections(rad).filter((con) =>
    con.interactions.some((id) => siblingPartThreadInteractions.includes(id)),
  );
  const siblingConnectedInteractions = siblingConnections.flatMap(
    (con) => con.interactions,
  );
  const siblingParentThreads = siblingConnectedInteractions
    .map((id) => {
      const activity = getActivity(rad)(id);
      return getThread(rad)(activity.thread_id);
    })
    .map((t) => ({ id: t.id, parent_id: t.parent_id, class: t.class }))
    .flat()
    .filter(Boolean);

  const excludeThreads = siblingParentThreads.map((t) => t.id);
  const excludeRefinementPrestates = siblingParentThreads
    .filter((t) => ["part", "case"].includes(t.class))
    .map((t) => getActivity(rad)(t.parent_id).prestate_id)
    .flat();
  const excludeSiblingParentThreadActivities = siblingParentThreads
    .map((t) => getThreadActivities(rad)(t.id))
    .flat()
    .map((a) => [a.id, a.prestate_id, a.poststate_id])
    .flat()
    .filter(Boolean);

  const ancestorThreads = siblingParentThreads
    .map((t) => getAncestorThreads(rad)(t.id))
    .flat();
  ancestorThreads.map((t) => {
    const activities = getThreadActivities(rad)(t.id);
    const activityIds = activities
      .map((a) => [a.id, a.prestate_id, a.poststate_id])
      .flat()
      .filter(Boolean);
    excludeSiblingParentThreadActivities.push(...activityIds);
  });

  const excludeElements = [
    ...new Set([
      ...excludeActivities,
      ...excludePartThreads,
      ...excludeThreads,
      ...excludeRefinementPrestates,
      ...excludeSiblingParentThreadActivities,
      ...untargetAncestors,
      ...untargetSuccessors,
    ]),
  ].filter(Boolean);

  const viableInteractions = allActivities
    .filter((a) => a.type === "interaction")
    .filter((a) => !connectedInteractions.includes(a.id))
    .filter((a) => !excludeElements.includes(a.id))
    .map((a) => a.id);

  const viableThreads = allPartThreads
    .filter((a) => !excludeElements.includes(a.id))
    .map((a) => a.id);

  const viableStates = allActivities
    .map((a) => [a.prestate_id, a.poststate_id])
    .flat()
    .filter(
      (s) => s !== null && s !== undefined && !excludeElements.includes(s),
    )
    .flat();

  return [
    ...viableRoleIDs,
    ...viableInteractions,
    ...viableStates,
    ...viableThreads,
  ];
};

const getCaseTargets = (rad) => (source) => {
  if (!source?.id) return [];

  const parentRole = getParentRole(rad)(source.id);
  if (!parentRole) return [];

  const parentRefinement = getActivity(rad)(source.parent_id);
  if (!parentRefinement) return [];

  const activities = getThreadSequence(rad)(source.id, source.prestate_id);
  const firstState = activities.length
    ? getStateById(rad)(activities[0].poststate_id)
    : getStateById(rad)(parentRefinement.poststate_id);
  const coarseTargets = evaluateStateTargetRules(rad, firstState).allowedIds;

  const allPartThreads = getAllPartThreadsWithRole(rad);
  const sourceRolePartThreads = allPartThreads.filter(
    (t) =>
      t.parentRoleId === getParentRole(rad)(parentRefinement.thread_id)?.id,
  );
  const excludePartThreads = [...sourceRolePartThreads.map((t) => t.id)].flat();

  const siblingPartThreads = getSubthreads(rad)(parentRefinement.id).filter(
    (t) => t.id !== source.id,
  );
  const siblingPartThreadInteractions = siblingPartThreads
    .map((t) => getThreadActivities(rad)(t.id))
    .flat()
    .filter((a) => a.type === "interaction")
    .map((a) => a.id);
  const siblingConnections = getConnections(rad).filter((con) =>
    con.interactions.some((id) => siblingPartThreadInteractions.includes(id)),
  );
  const siblingConnectedInteractions = siblingConnections.flatMap(
    (con) => con.interactions,
  );
  const siblingParentThreads = siblingConnectedInteractions
    .map((id) => {
      const activity = getActivity(rad)(id);
      return getThread(rad)(activity.thread_id);
    })
    .map((t) => ({ id: t.id, parent_id: t.parent_id, class: t.class }))
    .flat()
    .filter(Boolean);

  const excludeThreads = siblingParentThreads.map((t) => t.id);
  const excludeRefinementPrestates = siblingParentThreads
    .filter((t) => ["part", "case"].includes(t.class))
    .map((t) => getActivity(rad)(t.parent_id).prestate_id)
    .flat();
  const excludeSiblingParentThreadActivities = siblingParentThreads
    .map((t) => getThreadActivities(rad)(t.id))
    .flat()
    .map((a) => [a.id, a.prestate_id, a.poststate_id])
    .flat()
    .filter(Boolean);

  const ancestorThreads = siblingParentThreads
    .map((t) => getAncestorThreads(rad)(t.id))
    .flat();
  ancestorThreads.map((t) => {
    const activities = getThreadActivities(rad)(t.id);
    const activityIds = activities
      .map((a) => [a.id, a.prestate_id, a.poststate_id])
      .flat()
      .filter(Boolean);
    excludeSiblingParentThreadActivities.push(...activityIds);
  });

  const excludeElements = [
    ...new Set([
      ...excludePartThreads,
      ...excludeThreads,
      ...excludeRefinementPrestates,
      ...excludeSiblingParentThreadActivities,
    ]),
  ].filter(Boolean);

  return coarseTargets.filter((a) => !excludeElements.includes(a));
};

const evaluateRefinementTargetRules = (
  rad,
  source,
  sourceClass,
  ruleReason,
) => {
  const resolver =
    sourceClass === "case" ? getCaseTargets(rad) : getPartTargets(rad);
  let allowedIds = resolver(source);
  if (sourceClass === "case" || sourceClass === "part") {
    const caseThreadIds = new Set(
      (rad.threads ?? [])
        .filter((thread) => thread.class === "case")
        .map((thread) => thread.id),
    );
    allowedIds = allowedIds.filter((id) => !caseThreadIds.has(id));
  }
  const allowedSet = new Set(allowedIds);
  const exclusionReasons = {};
  const index = buildTargetIndex(rad);
  const sourceRoleId = resolveSourceRoleId(rad, source);

  for (const targetId of collectCandidateTargetIds(rad)) {
    if (allowedSet.has(targetId)) continue;
    if (targetId === source.id) {
      exclusionReasons[targetId] = "source_self";
      continue;
    }
    const targetRoleId = resolveTargetRoleId(rad, index, targetId);
    if (sourceRoleId && targetRoleId && sourceRoleId === targetRoleId) {
      exclusionReasons[targetId] = "same_role_scope";
      continue;
    }
    if (
      index.subthreadIds.has(targetId) ||
      index.interactionIds.has(targetId) ||
      index.stateIds.has(targetId)
    ) {
      exclusionReasons[targetId] = "refinement_crossing_scope";
      continue;
    }
    exclusionReasons[targetId] = ruleReason;
  }

  return { allowedIds, exclusionReasons };
};

const evaluateActivityTargetRules = (rad, source) => {
  const activity = getActivity(rad)(source.id);
  if (!activity || activity.type === "interaction") {
    return { allowedIds: [], exclusionReasons: {} };
  }

  const exclusionReasons = {};
  const index = buildTargetIndex(rad);
  const sourceRoleId = resolveSourceRoleId(rad, source);

  for (const targetId of collectCandidateTargetIds(rad)) {
    if (targetId === source.id) {
      exclusionReasons[targetId] = "source_self";
      continue;
    }
    const targetRoleId = resolveTargetRoleId(rad, index, targetId);
    if (sourceRoleId && targetRoleId && sourceRoleId === targetRoleId) {
      exclusionReasons[targetId] = "same_role_scope";
      continue;
    }
    exclusionReasons[targetId] = "excluded_by_activity_rules";
  }

  return { allowedIds: [], exclusionReasons };
};

const resolveTargetIdsForConnectionSource = (rad, source) => {
  const connection = (rad.connections ?? []).find(
    (con) => con.id === source.id,
  );
  if (!connection) {
    return {
      allowedIds: [],
      warnings: ["connection_not_found"],
      exclusionReasons: {},
    };
  }

  const allowedIds = getConnectionTargets(rad)(connection);
  const allowedSet = new Set(allowedIds);
  const exclusionReasons = {};
  const index = buildTargetIndex(rad);
  const scope = resolveConnectionScope(rad, {
    id: connection.id,
    class: "connection",
  });

  for (const targetId of collectCandidateTargetIds(rad)) {
    if (allowedSet.has(targetId)) continue;
    if (targetId === source.id) {
      exclusionReasons[targetId] = "source_self";
      continue;
    }
    if (scope?.interactionIds.has(targetId)) {
      exclusionReasons[targetId] = "connection_member";
      continue;
    }
    const targetRoleId = resolveTargetRoleId(rad, index, targetId);
    if (targetRoleId && scope?.roleIds.has(targetRoleId)) {
      exclusionReasons[targetId] = "connection_role_scope";
      continue;
    }
    exclusionReasons[targetId] = "excluded_by_connection_rules";
  }

  return { allowedIds, warnings: [], exclusionReasons };
};

const resolveTargetIdsForInteractionSource = (rad, source) => {
  const connection = getConnectionByInteraction(rad)(source.id);
  if (!connection) {
    return {
      allowedIds: [],
      warnings: ["interaction_connection_not_found"],
      exclusionReasons: {},
    };
  }
  debugTargets("getTargetConnections - interaction connection", connection);
  return resolveTargetIdsForConnectionSource(rad, {
    id: connection.id,
    class: "connection",
  });
};

const SOURCE_TARGET_RESOLVERS = {
  role: (rad, source) => {
    const evaluated = evaluateRoleTargetRules(rad, source);
    return {
      allowedIds: evaluated.allowedIds,
      warnings: [],
      exclusionReasons: evaluated.exclusionReasons,
    };
  },
  state: (rad, source) => {
    const evaluated = evaluateStateTargetRules(rad, source);
    return {
      allowedIds: evaluated.allowedIds,
      warnings: evaluated.warnings ?? [],
      exclusionReasons: evaluated.exclusionReasons,
      trace: evaluated.trace,
    };
  },
  activity: (rad, source) => {
    const evaluated = evaluateActivityTargetRules(rad, source);
    return {
      allowedIds: evaluated.allowedIds,
      warnings: [],
      exclusionReasons: evaluated.exclusionReasons,
    };
  },
  case: (rad, source) => {
    const evaluated = evaluateRefinementTargetRules(
      rad,
      source,
      "case",
      "excluded_by_case_rules",
    );
    return {
      allowedIds: evaluated.allowedIds,
      warnings: [],
      exclusionReasons: evaluated.exclusionReasons,
    };
  },
  part: (rad, source) => {
    const evaluated = evaluateRefinementTargetRules(
      rad,
      source,
      "part",
      "excluded_by_part_rules",
    );
    return {
      allowedIds: evaluated.allowedIds,
      warnings: [],
      exclusionReasons: evaluated.exclusionReasons,
    };
  },
  connection: resolveTargetIdsForConnectionSource,
  repeat: () => ({
    allowedIds: [],
    warnings: ["repeat_unimplemented"],
    exclusionReasons: {},
  }),
};

export const resolveTargetQuery = (rad, source) => {
  if (!source?.id || !source?.class) {
    return {
      allowedIds: [],
      warnings: ["invalid_source"],
      exclusionReasons: {},
    };
  }

  const hydratedSource = hydrateThreadSource(rad, source);
  if (hydratedSource?.type === "interaction") {
    console.log("Resolving targets for interaction source", hydratedSource);
    return resolveTargetIdsForInteractionSource(rad, source);
  }

  const resolver = SOURCE_TARGET_RESOLVERS[hydratedSource.class];
  if (!resolver) {
    return {
      allowedIds: [],
      warnings: ["unsupported_source_class"],
      exclusionReasons: {},
    };
  }
  console.log("Resolving targets for source", hydratedSource);
  return resolver(rad, hydratedSource);
};
